import { Box, Button, Checkbox, Chip, Divider, IconButton, LinearProgress, ListItemText, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import { ToolsListing } from '../../../../../components/custom';
import { LayoutBase } from '../../../../../layouts';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomAlert, VMenu } from '../../../../../components/shared';
import { useEffect, useState, useMemo } from 'react';

import { AnomaliaList, AnomalyService, IActionPlanSummary, IDetalhesAnomalia } from '../../../../../services/api/anomaly/AnomalyService';

import { useAuthContext } from '../../../../../context';
import { PlanosDeAcaoService } from '../../../../../services/api/planosDeAcao/PlanosDeAcaoService';
import { formatDateWhitoutHour } from '../../../../../services/helpers/helpers';
import { Add, FilterList, Visibility } from '@mui/icons-material';
import {
    ACTION_PLAN_STATUS_META,
    ActionPlanOrderBy,
    ActionPlanOrderDirection,
    ActionPlanStatus,
    ALL_ACTION_PLAN_STATUSES,
    filterActionPlans,
    sortActionPlans,
} from './actionPlanTableSort';

export const ACtionPlanPage = () => {
    const { id: barrageId } = useParams();
    const { userData } = useAuthContext();
    const navigate = useNavigate();
    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const pagePermissions = userData.nivel_acesso?.acompanhamentos?.plano;

    //Estados de anomalia e plano
    const [dataAnomalia, setDataAnomalia] = useState<IDetalhesAnomalia[]>([]);
    const [dataPlano, setDataPlano] = useState<IActionPlanSummary[]>([]);

    //estados de loading das tabelas
    const [loadingPlano, setLoadingPlano] = useState<boolean>(true);
    const [loadingAnomalia, setLoadingAnomalia] = useState<boolean>(true);
    const [planOrderBy, setPlanOrderBy] = useState<ActionPlanOrderBy>('data_limite_seguranca');
    const [planOrderDirection, setPlanOrderDirection] = useState<ActionPlanOrderDirection>('asc');
    const [visibleStatuses, setVisibleStatuses] = useState<ActionPlanStatus[]>(ALL_ACTION_PLAN_STATUSES);
    const [statusFilterAnchor, setStatusFilterAnchor] = useState<HTMLElement | null>(null);

    async function fetchDataPlano(uuid_barragem: string) {
        setLoadingPlano(prev => prev !== true && true);
        const res = await PlanosDeAcaoService.listPlanos(uuid_barragem);
        if (res instanceof Error) {
            setAlertInfo({ open: true, message: 'Erro ao buscar dados de plano de ação', severity: 'warning' });
            setLoadingPlano(prev => !prev);
        } else {
            await fetchDataAnomalia(uuid_barragem);
            setDataPlano(res);
            setLoadingPlano(false);
        }
    }

    async function fetchDataAnomalia(uuid_barragem: string) {
        setLoadingPlano(prev => prev !== true && true);
        const res = await AnomalyService.list(uuid_barragem);
        if (res instanceof Error) {
            setAlertInfo({ open: true, message: 'Erro ao buscar dados de anomalia', severity: 'warning' });
            setLoadingAnomalia(prev => !prev);
        } else {
            const responseData = res as AnomaliaList[];
            const anomaliasEstrutura = responseData.flatMap(item => item.detalhes);
            const anomaliasFiltradas = anomaliasEstrutura.filter(item => item.situacao !== '' && item.plano_acao?.length == 0);
            setDataAnomalia(anomaliasFiltradas);
            setLoadingAnomalia(false);
        }
    }

    useEffect(() => {
        barrageId && fetchDataPlano(barrageId);
        // barrageId && fetchDataAnomalia(barrageId);
    }, []);

    function getSituacao(situacoa: string) {
        if (situacoa.includes('NE')) return 'Não existente';
        if (situacoa.includes('NI')) return 'Não Inspecionado';
        if (situacoa.includes('PV')) return '1ª Ocorrência';
        if (situacoa.includes('RE')) return 'Recorrente';
        if (situacoa.includes('PC')) return 'Constante';
        if (situacoa.includes('TR')) return 'Tratada';
        if (situacoa.includes('AU')) return 'Aumentou';
    }

    function newAnomalia(uuid_anomalia: string) {
        navigate(`./novo/${uuid_anomalia}`);
    }

    function handleRequestPlanSort(column: ActionPlanOrderBy) {
        const isAscending = planOrderBy === column && planOrderDirection === 'asc';
        setPlanOrderBy(column);
        setPlanOrderDirection(isAscending ? 'desc' : 'asc');
    }

    function handleToggleStatusVisibility(status: ActionPlanStatus) {
        setVisibleStatuses(prev => (
            prev.includes(status)
                ? prev.filter(item => item !== status)
                : [...prev, status]
        ));
    }

    function handleOpenStatusFilter(event: React.MouseEvent<HTMLElement>) {
        event.stopPropagation();
        setStatusFilterAnchor(event.currentTarget);
    }

    function handleCloseStatusFilter() {
        setStatusFilterAnchor(null);
    }

    const filteredPlanData = useMemo(
        () => filterActionPlans(dataPlano, visibleStatuses),
        [dataPlano, visibleStatuses],
    );

    const sortedPlanData = useMemo(
        () => sortActionPlans(filteredPlanData, planOrderBy, planOrderDirection),
        [filteredPlanData, planOrderBy, planOrderDirection],
    );

    return (
        <LayoutBase
            title='Planos de ação'
            tollBar={
                <ToolsListing
                    showBackButton
                    mostrarInputBusca={false}
                />
            }
        >
            <VMenu id={barrageId} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5, padding: 2 }}>
                {/* Box de plano de ação */}
                <Box>
                    {dataPlano.length == 0 && loadingPlano && (
                        <LinearProgress />
                    )}
                    {dataPlano.length == 0 && !loadingPlano && (
                        <Typography textAlign='center'>Nenhum plano de ação cadastrado no sistema.</Typography>
                    )}
                    {dataPlano.length > 0 && !loadingPlano && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '400px' }}>
                            <div>
                                <Typography variant='h5'>Planos de ação</Typography>
                                <Divider />
                            </div>
                            <TableContainer component={Paper}>
                                <Table sx={{ boder: '1px solid #ccc', borderRadius: 25, padding: 3, overflowY: 'auto' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Cod. Anomalia</TableCell>
                                            <TableCell sortDirection={planOrderBy === 'acao' ? planOrderDirection : false}>
                                                <TableSortLabel
                                                    active={planOrderBy === 'acao'}
                                                    direction={planOrderBy === 'acao' ? planOrderDirection : 'asc'}
                                                    onClick={() => handleRequestPlanSort('acao')}
                                                >
                                                    Natureza da ação
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>Responsável</TableCell>
                                            <TableCell>NPA</TableCell>
                                            <TableCell sortDirection={planOrderBy === 'status' ? planOrderDirection : false}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <TableSortLabel
                                                        active={planOrderBy === 'status'}
                                                        direction={planOrderBy === 'status' ? planOrderDirection : 'asc'}
                                                        onClick={() => handleRequestPlanSort('status')}
                                                    >
                                                        Situação
                                                    </TableSortLabel>
                                                    <IconButton size='small' onClick={handleOpenStatusFilter}>
                                                        <FilterList
                                                            fontSize='small'
                                                            color={visibleStatuses.length < ALL_ACTION_PLAN_STATUSES.length ? 'primary' : 'inherit'}
                                                        />
                                                    </IconButton>
                                                    <Menu
                                                        anchorEl={statusFilterAnchor}
                                                        open={Boolean(statusFilterAnchor)}
                                                        onClose={handleCloseStatusFilter}
                                                    >
                                                        <MenuItem onClick={() => setVisibleStatuses(ALL_ACTION_PLAN_STATUSES)}>
                                                            Selecionar todos
                                                        </MenuItem>
                                                        <MenuItem onClick={() => setVisibleStatuses([])}>
                                                            Limpar seleção
                                                        </MenuItem>
                                                        <Divider />
                                                        {ALL_ACTION_PLAN_STATUSES.map(status => (
                                                            <MenuItem
                                                                key={status}
                                                                onClick={() => handleToggleStatusVisibility(status)}
                                                            >
                                                                <Checkbox
                                                                    size='small'
                                                                    checked={visibleStatuses.includes(status)}
                                                                />
                                                                <ListItemText primary={ACTION_PLAN_STATUS_META[status].label} />
                                                            </MenuItem>
                                                        ))}
                                                    </Menu>
                                                </Box>
                                            </TableCell>
                                            <TableCell sortDirection={planOrderBy === 'data_limite_seguranca' ? planOrderDirection : false}>
                                                <TableSortLabel
                                                    active={planOrderBy === 'data_limite_seguranca'}
                                                    direction={planOrderBy === 'data_limite_seguranca' ? planOrderDirection : 'asc'}
                                                    onClick={() => handleRequestPlanSort('data_limite_seguranca')}
                                                >
                                                    Data limite
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell sortDirection={planOrderBy === 'origem' ? planOrderDirection : false}>
                                                <TableSortLabel
                                                    active={planOrderBy === 'origem'}
                                                    direction={planOrderBy === 'origem' ? planOrderDirection : 'asc'}
                                                    onClick={() => handleRequestPlanSort('origem')}
                                                >
                                                    Origem
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align='center'>Ações</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedPlanData.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={7} align='center'>
                                                    Nenhum plano de ação para os status selecionados.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {sortedPlanData.length > 0 && sortedPlanData.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item?.anomalia?.codigo_anomalia ? item?.anomalia?.codigo_anomalia : '----'}</TableCell>
                                                <TableCell>{item.acao}</TableCell>
                                                <TableCell>{item.responsavel_principal}</TableCell>
                                                <TableCell>{item.npa}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={item.status ? ACTION_PLAN_STATUS_META[item.status].label : ACTION_PLAN_STATUS_META['NAO INICIADO'].label}
                                                        color={item.status ? ACTION_PLAN_STATUS_META[item.status].color : ACTION_PLAN_STATUS_META['NAO INICIADO'].color}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{formatDateWhitoutHour(item.data_limite_seguranca)}</TableCell>
                                                <TableCell>{item.origem.toUpperCase()}</TableCell>
                                                <TableCell align='center'>
                                                    <Button variant='outlined' startIcon={<Visibility />} onClick={() => navigate(`./editar/${item.uuid}`)}>Gerenciar plano</Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Box>

                {/* Box de anomalias */}
                <Box>
                    {dataAnomalia.length == 0 && loadingAnomalia && (
                        <LinearProgress />
                    )}
                    {dataAnomalia.length == 0 && !loadingAnomalia && (
                        <Typography textAlign='center'>Nenhuma anomalia cadastrado no sistema ou sem plano de ação.</Typography>
                    )}
                    {dataAnomalia.length > 0 && !loadingAnomalia && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '400px' }}>
                            <div>
                                <Typography variant='h5'>Anomalias</Typography>
                                <Divider />
                            </div>
                            <TableContainer component={Paper}>
                                <Table sx={{ boder: '1px solid #ccc', borderRadius: 25, padding: 3, overflowY: 'auto' }}>
                                    <TableHead>
                                        <TableCell>Anomalia</TableCell>
                                        <TableCell>Situação</TableCell>
                                        <TableCell>Magnitude</TableCell>
                                        <TableCell>NPA</TableCell>
                                        <TableCell>Data de inspeção</TableCell>
                                        <TableCell>Origem</TableCell>
                                        <TableCell align='center'>Ações</TableCell>
                                    </TableHead>
                                    <TableBody>
                                        {dataAnomalia.length > 0 && dataAnomalia.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.item}</TableCell>
                                                <TableCell>{item.situacao !== '' ? getSituacao(item.situacao) : '---'}</TableCell>
                                                <TableCell>{item.magnitude !== '' ? item.magnitude : '---'}</TableCell>
                                                <TableCell>{item.npa !== '' ? item.npa : '---'}</TableCell>
                                                <TableCell>{formatDateWhitoutHour(item.data_inspecao)}</TableCell>
                                                <TableCell>{item.origem?.toUpperCase()}</TableCell>
                                                <TableCell align='center'>
                                                    <Button variant='outlined' disabled={!pagePermissions?.includes('add')} startIcon={<Add />} onClick={() => newAnomalia(item.uuid !== undefined ? item.uuid : '')}>Criar plano</Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Box>
                {/* </>
                )} */}
            </Box>
            {alertInfo.open && (
                <CustomAlert
                    severity={alertInfo.severity}
                    message={alertInfo.message}
                />
            )}
        </LayoutBase>
    );
};
